import {
  Box,
  Checkbox,
  CheckboxCheckedChangeDetails,
  Collapsible,
  Flex,
  Text,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { memo, useCallback, useMemo } from 'react';

import { allAttributes } from 'helpers/fakeData';
import { useBoolean } from 'hooks/useBoolean';
import { useStores } from 'stores/rootStore';
import RawArrowIcon from 'vectors/Arrowhead';

const isColorDimension = (dimensionId: string) => dimensionId === 'color';

const ColorFilters = () => {
  const [isOpen, setIsOpen] = useBoolean(true);

  return (
    <Collapsible.Root unmountOnExit open={isOpen} onOpenChange={setIsOpen.toggle}>
      <Collapsible.Trigger paddingY="3" cursor="pointer">
        <Flex alignItems="center" gap="2">
          <Text fontWeight="medium">Color</Text>
          <RawArrowIcon boxSize="8px" direction={isOpen ? 'up' : 'down'} />
        </Flex>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <ColorsList />
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

const ColorsList = observer(() => {
  const { marketPlaceStore } = useStores();

  const colors = useMemo(() => {
    return allAttributes.filter((attribute) => isColorDimension(attribute.dimensionId));
  }, []);

  const onCheckedChange = useCallback(
    (attributeId: string) => (details: CheckboxCheckedChangeDetails) => {
      if (details.checked !== 'indeterminate' && details.checked) {
        marketPlaceStore.addFilterAttributeId(attributeId);
      } else {
        marketPlaceStore.removeFilterAttributeId(attributeId);
      }
    },
    [marketPlaceStore],
  );

  return (
    <Flex flexDir="column" gap={2}>
      {colors.map((color) => (
        <Checkbox.Root
          key={color.value}
          cursor="pointer"
          onCheckedChange={onCheckedChange(color.id)}
          defaultChecked={marketPlaceStore.selectedFilterAttributeIds.includes(color.id)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control borderColor="border.light">
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.Label>
            <Flex alignItems="center" gap="2">
              <Text fontWeight="light">{color.description}</Text>
              <Box
                borderColor="border.light"
                backgroundColor={color.value}
                boxSize="16px"
                borderRadius="full"
                borderWidth="1px"
              />
            </Flex>
          </Checkbox.Label>
        </Checkbox.Root>
      ))}
    </Flex>
  );
});

export default memo(ColorFilters);
